/*
  STR8 PARALLAX v4.0 — pinned stage, per-layer timing windows, 3D and blur

  This is the Osmo pattern: a tall wrapper holds a `position: sticky` stage
  pinned to the top of the viewport. While the wrapper scrolls past, the
  stage sits still and its own layers move at different fractions of that
  scroll, which is what actually reads as depth rather than the whole block
  drifting a few pixels. When the wrapper runs out, the stage un-pins on its
  own and the next section's own background scrolls up and covers it, curtain
  style. None of that second part is JavaScript. It is what `position:
  sticky` does for free once the next element is opaque and stacked above it,
  which is why the CSS gives that element rounded top corners and a shadow.

  v1 measured a stage travelling through the viewport and nudged it a little.
  That read as barely-there. This measures the WRAPPER's own scroll fraction
  while the stage is pinned, 0 the moment pinning starts to 1 the moment it
  is about to release, and layers are told to move a full multiple of that,
  not a fraction of it. That is the difference between visible and not.

  WHY THIS IS STILL NOT A PERMANENT rAF LOOP
  assets/str8-marquee.js runs a loop because a marquee animates on its own
  and has work every frame regardless of the visitor. A pinned stage only has
  work when the page moves, so this listens for scroll and schedules exactly
  one frame, refusing to queue a second while one is pending.

  REDUCED MOTION
  Handled almost entirely in CSS: the stylesheet drops position: sticky back
  to static and collapses the wrapper's extra height, so the pin and the
  curtain cover it enables never happen and the sections simply stack. This
  script additionally never binds a listener and never writes a transform, so
  there is nothing left running even if a layer's authored position were
  visible.

  TIMING WINDOWS
  v3 gives every layer its own slice of the pinned scroll rather than making
  them all run across the whole of it. That is what allows one pinned stage
  to hold two sequential scenes: the title block animates out across the
  first third, the story block animates in just behind it, and the story then
  holds for the remaining travel. Opacity is a from/to pair rather than a
  fade-out-only value, because a scene arriving needs to fade IN.

  WHAT v4 ADDED, AND WHY
  Rotation and blur, both optional and both null unless the markup asks for
  them, so every layer authored against v3 behaves identically. The invite
  page needs them: its hero centrepiece is an invitation card that has to
  tilt in three dimensions and fall away as it exits, and a flat translate
  cannot sell a physical object. Blur is the cheap half of a depth-of-field
  and is what stops a departing scene competing with the one arriving.

  rotateX and rotateY do nothing without a perspective on an ANCESTOR of the
  layer. Set it in CSS on the stage or on the layer's own parent, never on
  the layer, because this script owns that element's transform property and
  a perspective() written into the transform would be overwritten every
  frame.

  USAGE
    data-str8-pin-wrap    the tall outer element
    data-str8-pin-stage   the sticky child, one only
    data-str8-pin-layer   on each layer, with any of:

      data-from / data-to                 translateY in px
      data-scale-from / data-scale-to     scale
      data-opacity-from / data-opacity-to opacity
      data-rot-from / data-rot-to         rotateZ in degrees
      data-rotx-from / data-rotx-to       rotateX in degrees
      data-roty-from / data-roty-to       rotateY in degrees
      data-blur-from / data-blur-to       blur in px
      data-start / data-end               the slice of the wrapper's 0..1
                                          progress this layer animates over.
                                          Defaults to the whole 0..1.

  A layer is pinned at its "from" values before its window opens and at its
  "to" values after it closes, so scenes hold rather than drift.

  Layers nest. A card inside a scene can carry its own data-str8-pin-layer
  with its own window, and its transform composes with its parent's, which is
  how one pinned stage gets a scene that moves as a whole and an object
  inside it that moves differently.
*/
(function () {
  'use strict';

  if (window.__str8ParallaxLoaded) return;
  window.__str8ParallaxLoaded = true;

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }

  function PinStage(wrap) {
    this.wrap = wrap;
    this.stage = wrap.querySelector('[data-str8-pin-stage]');
    if (!this.stage) return;

    var nodes = wrap.querySelectorAll('[data-str8-pin-layer]');
    this.layers = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var start = el.hasAttribute('data-start') ? parseFloat(el.getAttribute('data-start')) : 0;
      var end = el.hasAttribute('data-end') ? parseFloat(el.getAttribute('data-end')) : 1;
      /* A zero-width window would divide by zero when normalising. */
      if (!(end > start)) { start = 0; end = 1; }

      /* A pair is only honoured when BOTH ends are present, so a half
         authored attribute is ignored rather than silently animating from
         zero to something. */
      function pair(a, b) {
        if (!el.hasAttribute(a) || !el.hasAttribute(b)) return null;
        return [parseFloat(el.getAttribute(a)), parseFloat(el.getAttribute(b))];
      }

      this.layers.push({
        el: el,
        from: parseFloat(el.getAttribute('data-from')) || 0,
        to: parseFloat(el.getAttribute('data-to')) || 0,
        scale: pair('data-scale-from', 'data-scale-to'),
        opacity: pair('data-opacity-from', 'data-opacity-to'),
        rotZ: pair('data-rot-from', 'data-rot-to'),
        rotX: pair('data-rotx-from', 'data-rotx-to'),
        rotY: pair('data-roty-from', 'data-roty-to'),
        blur: pair('data-blur-from', 'data-blur-to'),
        start: start,
        end: end
      });
    }
    if (!this.layers.length) return;

    this.visible = false;
    this.pending = false;
    this.onScroll = this.request.bind(this);
    this.frame = this.draw.bind(this);
    this.bind();
  }

  PinStage.prototype.bind = function () {
    var self = this;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (self.visible) self.request();
      }, { rootMargin: '25% 0px 25% 0px' }).observe(this.wrap);
    } else {
      this.visible = true;
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    this.visible = true;
    this.request();
  };

  PinStage.prototype.request = function () {
    if (this.pending || !this.visible) return;
    this.pending = true;
    requestAnimationFrame(this.frame);
  };

  PinStage.prototype.draw = function () {
    this.pending = false;

    var rect = this.wrap.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var span = rect.height - vh;

    /* Bail rather than divide by zero, or by a negative span on a wrapper
       shorter than the viewport. Doing nothing is correct until there is a
       real distance to travel. */
    if (!vh || span <= 0) return;

    var progress = clamp(-rect.top / span, 0, 1);

    for (var i = 0; i < this.layers.length; i++) {
      var l = this.layers[i];

      /* Normalise the wrapper's progress into this layer's own window, so it
         sits still at "from" before the window opens and at "to" after it
         closes rather than drifting through the whole scroll. */
      var p = clamp((progress - l.start) / (l.end - l.start), 0, 1);

      var y = lerp(l.from, l.to, p);
      var t = 'translate3d(0, ' + y.toFixed(2) + 'px, 0)';

      /* Order matters. Translate first so the move is unaffected by the
         rotations, then scale, then the rotations outermost so a tilted
         card pivots about its own centre rather than swinging. */
      if (l.scale) { t += ' scale(' + lerp(l.scale[0], l.scale[1], p).toFixed(4) + ')'; }
      if (l.rotX)  { t += ' rotateX(' + lerp(l.rotX[0], l.rotX[1], p).toFixed(3) + 'deg)'; }
      if (l.rotY)  { t += ' rotateY(' + lerp(l.rotY[0], l.rotY[1], p).toFixed(3) + 'deg)'; }
      if (l.rotZ)  { t += ' rotate(' + lerp(l.rotZ[0], l.rotZ[1], p).toFixed(3) + 'deg)'; }

      l.el.style.transform = t;

      if (l.blur) {
        var b = lerp(l.blur[0], l.blur[1], p);
        /* Writing filter: blur(0px) still promotes the element and costs a
           composite every frame, so drop the property entirely at zero. */
        l.el.style.filter = b > 0.05 ? 'blur(' + b.toFixed(2) + 'px)' : '';
      }

      if (l.opacity) {
        var o = lerp(l.opacity[0], l.opacity[1], p);
        l.el.style.opacity = o.toFixed(3);
        /* A fully faded scene must not swallow clicks meant for the one
           above it, and must not be read out by a screen reader while it is
           invisible. */
        l.el.style.visibility = o < 0.02 ? 'hidden' : 'visible';
      }
    }
  };

  function init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var wraps = document.querySelectorAll('[data-str8-pin-wrap]');
    for (var i = 0; i < wraps.length; i++) {
      if (wraps[i].__str8Pin) continue;
      wraps[i].__str8Pin = new PinStage(wraps[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
